/**
 * @author: lv.chen
 * @date: 2025/11/18
 * @desc: CasualGamesReports页面
 */
import React, { useEffect } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import ReuseTabPageTemplate from '@intelligence/v3/components/ReuseTabPageTemplate';
import MarketReportsTab from './components/MarketReportsTab';
import AnalysisReportsTab from './components/AnalysisReportsTab';
import useUrlSyncActiveId from '@intelligence/v3/_hooks/useUrlSyncActiveId';
import useCondConfigs from '@intelligence/v3/_hooks/useCondConfigs';
import { buriedPointPage } from '@home/utils/buriedPoint';
import { dbiPageLogMes } from '@intelligence/utils/buriedPointCfg';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

interface ICasualGamesReportsProps extends IBaseProps {}

const TAB_ID_MAP = {
  MARKET_REPORTS: 'marketReports',
  ANALYSIS_REPORTS: 'analysisReports',
};
const TAB_IDS = Object.values(TAB_ID_MAP);
const DEFAULT_ACTIVE_TAB_ID = TAB_ID_MAP.MARKET_REPORTS;

/**
 * 页面打点key映射
 * 键：页面tab的id
 * 值：页面打点配置的key
 */
const PAGE_LOG_KEY_MAPPING: IRecord<string> = {
  [TAB_ID_MAP.MARKET_REPORTS]: 'casualGamesReportsMarketReports',
  [TAB_ID_MAP.ANALYSIS_REPORTS]: 'casualGamesReportsAnalysisReports',
};

const analysisIds = (ids: string): string[] => {
  if (!ids) {
    return [];
  }

  return ids
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
};

const CasualGamesReports: React.FC<ICasualGamesReportsProps> = (props) => {
  const { style, className } = props;

  const { formatMessage } = useIntl();

  const { loading: reportIdsMapLoading, data: reportIdsMap } = useCondConfigs({
    queryParams: {
      casual_games_reports_ids: {
        target: 'game_config',
        params: {
          key: 'casual_games_*',
        },
      },
    },
  });

  const tabs = [
    {
      id: TAB_ID_MAP.MARKET_REPORTS,
      label: formatMessage({ id: 'dbi_reports_market_reports' }),
      className: '',
      component: (
        <MarketReportsTab
          loading={reportIdsMapLoading}
          reportIds={analysisIds(reportIdsMap?.casual_games_reports_ids?.casual_games_market_reports_ids)}
        />
      ),
    },
    {
      id: TAB_ID_MAP.ANALYSIS_REPORTS,
      label: formatMessage({ id: 'dbi_reports_analysis_reports' }),
      className: '',
      component: (
        <AnalysisReportsTab
          loading={reportIdsMapLoading}
          reportIds={analysisIds(reportIdsMap?.casual_games_reports_ids?.casual_games_analysis_reports_ids)}
        />
      ),
    },
  ];

  const [activeId, setActiveId] = useUrlSyncActiveId(TAB_IDS, DEFAULT_ACTIVE_TAB_ID);

  const onActive = (tab: { id: string }) => {
    const tabId = tab?.id || DEFAULT_ACTIVE_TAB_ID;
    // 只有当tab真正改变时才更新状态和URL
    if (tabId !== activeId) {
      setActiveId(tabId);
      history.replaceState(null, '', `/intelligence/casualGamesHub/reports/${tabId}`);
    }
  };

  useEffect(() => {
    const pageLogCfgKey = PAGE_LOG_KEY_MAPPING[activeId];
    const pageLogCfg = dbiPageLogMes[pageLogCfgKey];
    if (pageLogCfg) {
      buriedPointPage(pageLogCfg);
    }
  }, [activeId]);

  return (
    <ReuseTabPageTemplate
      style={style}
      className={classnames('dbi-casual-games-reports', className)}
      title={formatMessage({ id: 'dbi_reports_casual_games_reports_title' })}
      subTitle={formatMessage({ id: 'dbi_reports_casual_games_reports_sub_title' })}
      pageLogKeyMap={PAGE_LOG_KEY_MAPPING}
      tabs={tabs}
      activeId={activeId}
      onActive={onActive}
    >
      <MarqueeBanner />
      {tabs?.map((tab) => {
        const tabId = tab?.id || DEFAULT_ACTIVE_TAB_ID;
        return <div key={tabId}>{tabId === activeId && tab?.component}</div>;
      })}
    </ReuseTabPageTemplate>
  );
};

export default CasualGamesReports;
